import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCompanyMutation } from "@/hooks/useCompanyMutation";
import { supabase } from "@/integrations/supabase/client";
import type { Order } from "@/components/KanbanCard";
import { useState } from "react";

export type StatusTab = "ALL" | "PENDING" | "IN_PROGRESS" | "COMPLETED";

export const isPending = (q: Order) =>
  q.status === "ORCAMENTO" ||
  (q.is_quote && q.status === "AGUARDANDO" && !q.stage_timestamps?.APPROVED_AT);

export const isCompleted = (q: Order) => q.status === "PAGO";

export const isInProgress = (q: Order) => !isPending(q) && !isCompleted(q);

export function useQuotesData(activeCompanyId: string | null) {
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusTab, setStatusTab] = useState<StatusTab>("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;

  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ["quotes", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .eq("company_id", activeCompanyId)
        .or("is_quote.eq.true,status.eq.ORCAMENTO")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Order[];
    },
    enabled: !!activeCompanyId,
  });

  const convertToOrder = useCompanyMutation({
    mutationFn: async (quoteId: string) => {
      const nowIso = new Date().toISOString();
      const quote = quotes.find((q) => q.id === quoteId);
      const { error } = await supabase
        .from("orders")
        .update({
          status: "AGUARDANDO",
          is_quote: true,
          created_at: nowIso,
          stage_timestamps: {
            ...(quote?.stage_timestamps || {}),
            AGUARDANDO: nowIso,
            APPROVED_AT: nowIso,
          },
        })
        .eq("id", quoteId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotes", activeCompanyId] });
      queryClient.invalidateQueries({ queryKey: ["orders", activeCompanyId] });
      alert(
        "Orçamento aprovado com sucesso! O veículo entrou na fila do Pátio.",
      );
    },
    onError: (err: any) => {
      alert(`Erro ao converter orçamento: ${err.message}`);
    },
  });

  const deleteQuote = useCompanyMutation({
    mutationFn: async (quoteId: string) => {
      const { error } = await supabase
        .from("orders")
        .delete()
        .eq("id", quoteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotes", activeCompanyId] });
    },
  });

  return {
    quotes,
    isLoading,
    searchTerm,
    setSearchTerm,
    statusTab,
    setStatusTab,
    currentPage,
    setCurrentPage,
    ITEMS_PER_PAGE,
    convertToOrder,
    deleteQuote,
  };
}
